import { useId, useRef, useState } from 'react';
import type { ChangeEvent, DragEvent, KeyboardEvent, ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCloudArrowUp,
  faFilePdf,
  faFileLines,
  faCircleCheck,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { cn } from '@/lib/cn';

interface CustomerFileUploadProps {
  /** Visible label text; also the accessible name of the file input. */
  label: ReactNode;
  /** Id of the underlying `<input type="file">` (lets a parent `<label htmlFor>` bind). */
  id?: string;
  required?: boolean;
  /** e.g. "application/pdf,.pdf" — forwarded to the input. */
  accept?: string;
  /** Instruction copy under the icon before a file is chosen. */
  hint?: string;
  /** Extra guidance line (accepted type, size limit). */
  guidance?: string;
  file: File | null;
  error?: string | null;
  disabled?: boolean;
  onFileChange: (file: File | null) => void;
  /** Accessible name of the remove control. */
  removeLabel?: string;
  className?: string;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(bytes < 10 * 1024 ? 1 : 0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileTypeLabel(file: File): string {
  if (file.type === 'application/pdf' || /\.pdf$/i.test(file.name)) return 'PDF';
  const ext = file.name.split('.').pop();
  return ext && ext !== file.name ? ext.toUpperCase() : file.type || 'File';
}

/**
 * Custom file picker for customer forms. The native input stays in the DOM
 * (visually hidden, still labelled, still focusable) so keyboard, screen
 * reader, form validation and the existing tests all keep working; the
 * visible surface is a tap target on phones and a drop zone on desktop.
 * Selection state shows the file's icon, name, type and size with a remove
 * control; the parent owns validation and passes `error` back down.
 */
export function CustomerFileUpload({
  label,
  id,
  required = false,
  accept,
  hint = 'Tap to choose a file',
  guidance,
  file,
  error,
  disabled = false,
  onFileChange,
  removeLabel = 'Remove file',
  className = '',
}: CustomerFileUploadProps) {
  const autoId = useId();
  const inputId = id ?? `${autoId}-file`;
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const openPicker = () => {
    if (!disabled) inputRef.current?.click();
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onFileChange(e.target.files?.[0] ?? null);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    if (disabled) return;
    const dropped = e.dataTransfer.files?.[0] ?? null;
    if (dropped) onFileChange(dropped);
  };

  const handleKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openPicker();
    }
  };

  const clear = () => {
    if (inputRef.current) inputRef.current.value = '';
    onFileChange(null);
  };

  const hasError = Boolean(error);
  const describedBy = [guidance ? hintId : null, hasError ? errorId : null]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={className}>
      <label htmlFor={inputId} className="block text-sm font-medium text-customer-text">
        {label}
        {required && (
          <span className="ml-0.5 text-customer-danger-text" aria-hidden="true">
            *
          </span>
        )}
      </label>

      {/* The real input: visually hidden, never display:none, so it stays in
          the accessibility tree and keeps its label association. It carries
          aria-required but NOT the native `required` attribute: a file that
          arrived by drag-and-drop lives in React state, not in this input, and
          native constraint validation would then block the form on an
          invisible control. The owning form enforces completeness itself. */}
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={accept}
        aria-required={required || undefined}
        aria-invalid={hasError || undefined}
        aria-describedby={describedBy || undefined}
        disabled={disabled}
        onChange={handleChange}
        className="sr-only"
      />

      {!file ? (
        <div
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-disabled={disabled || undefined}
          aria-controls={inputId}
          onClick={openPicker}
          onKeyDown={handleKey}
          onDragOver={(e) => {
            e.preventDefault();
            if (!disabled) setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          className={cn(
            'customer-pressable mt-1.5 flex min-h-[7.5rem] cursor-pointer flex-col items-center justify-center gap-1.5 rounded-customer border-2 border-dashed px-4 py-5 text-center transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring focus-visible:ring-offset-2 focus-visible:ring-offset-customer-surface',
            hasError
              ? 'border-customer-danger/50 bg-customer-danger/[0.04]'
              : dragging
                ? 'border-customer-primary bg-customer-primary/[0.06]'
                : 'border-customer-border bg-customer-surface-muted/60 hover:border-customer-primary/60 hover:bg-customer-primary/[0.04]',
            disabled && 'cursor-not-allowed opacity-60',
          )}
        >
          <span
            className="flex h-10 w-10 items-center justify-center rounded-full bg-customer-primary/10 text-customer-primary-text"
            aria-hidden="true"
          >
            <FontAwesomeIcon icon={faCloudArrowUp} className="h-4 w-4" />
          </span>
          <span className="text-sm font-medium text-customer-text">
            {hint}
            <span className="hidden sm:inline"> or drag it here</span>
          </span>
          {guidance && (
            <span id={hintId} className="text-xs text-customer-text-secondary">
              {guidance}
            </span>
          )}
        </div>
      ) : (
        <div
          className={cn(
            'customer-slide-up mt-1.5 flex items-center gap-3 rounded-customer border px-3 py-2.5',
            hasError
              ? 'border-customer-danger/50 bg-customer-danger/[0.04]'
              : 'border-customer-success/40 bg-customer-success/[0.06]',
          )}
        >
          <span
            className={cn(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-customer-sm',
              fileTypeLabel(file) === 'PDF'
                ? 'bg-customer-danger/10 text-customer-danger-text'
                : 'bg-customer-text/[0.06] text-customer-text-secondary',
            )}
            aria-hidden="true"
          >
            <FontAwesomeIcon
              icon={fileTypeLabel(file) === 'PDF' ? faFilePdf : faFileLines}
              className="h-4 w-4"
            />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-customer-text" title={file.name}>
              {file.name}
            </p>
            <p className="mt-0.5 flex items-center gap-1.5 text-xs text-customer-text-secondary">
              {!hasError && (
                <FontAwesomeIcon
                  icon={faCircleCheck}
                  className="h-3 w-3 text-customer-success-text"
                  aria-hidden="true"
                />
              )}
              <span>
                {fileTypeLabel(file)} · {formatBytes(file.size)}
                {!hasError && ' · Ready to submit'}
              </span>
            </p>
          </div>
          <button
            type="button"
            onClick={clear}
            disabled={disabled}
            aria-label={removeLabel}
            className="customer-pressable flex h-11 w-11 shrink-0 items-center justify-center rounded-customer-sm text-customer-text-secondary hover:bg-customer-text/[0.06] hover:text-customer-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
          </button>
        </div>
      )}

      {hasError && (
        <p
          id={errorId}
          role="alert"
          className="mt-1.5 flex items-start gap-1.5 text-xs font-medium text-customer-danger-text"
        >
          {error}
        </p>
      )}
    </div>
  );
}
